// --- Types shared between Index.tsx and its modal components ---

export interface Plan {
  id: string
  name: string
  slug: string
  begin_date: string
  end_date: string
  revenue_target: number
  currency: string | null
  org_node_id: string | null
  is_current: boolean
  has_published_version: boolean
}

export interface OrgNode {
  id: string
  parent_id: string | null
  name: string
  slug: string
}

// --- Helpers shared between Index.tsx and its modal components ---

export function buildOrgNodeOptions(nodes: OrgNode[]): { id: string; label: string }[] {
  const byParent = new Map<string | null, OrgNode[]>()
  for (const n of nodes) {
    const key = n.parent_id
    if (!byParent.has(key)) byParent.set(key, [])
    byParent.get(key)!.push(n)
  }
  const ids = new Set(nodes.map((n) => n.id))
  const out: { id: string; label: string }[] = []
  const walk = (parentId: string | null, trail: string[]) => {
    const kids = byParent.get(parentId) || []
    for (const n of kids) {
      const next = [...trail, n.name]
      out.push({ id: n.id, label: next.join(' › ') })
      walk(n.id, next)
    }
  }
  // Roots are nodes with no parent_id, or whose parent isn't in `nodes` (e.g. the
  // top of a market subtree, whose real parent is the org root and is excluded).
  const rootParentIds = new Set<string | null>(
    nodes.filter((n) => n.parent_id === null || !ids.has(n.parent_id)).map((n) => n.parent_id)
  )
  for (const rootParentId of rootParentIds) walk(rootParentId, [])
  return out
}

export function formatPeriod(begin_date: string, end_date: string): string {
  const fmt = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${fmt(begin_date)} – ${fmt(end_date)}`
}

export function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

export function sanitizeSlugInput(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9-]/g, '')
}
